import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { Permission, Role, RoleDTO } from '@prism/contracts';
import { api } from '../client';
import { queryKeys } from '../queryKeys';
import { usePaged } from './paged';

export interface MemberDTO {
  id: string; email: string; displayName: string; disabled: boolean; role: Role; roleName?: string;
  grants: Permission[]; revokes: Permission[]; groups: string[]; permissions: Permission[]; lastSignInAt: string | null;
  /** 1.0.0-rc.4: two-step sign-in set up; signs in through an identity provider; has a PRISM password. */
  mfa?: boolean; sso?: boolean; hasPassword?: boolean;
}
export interface AccessGroupDTO { id: string; name: string; description: string; members: string[]; memberCount: number; assets: number }

/** The workspace's roles; administrators also get permissions and counts. */
export function useRoles(enabled = true) {
  return useQuery({ queryKey: queryKeys.roles(), queryFn: () => api<{ items: RoleDTO[] }>('/api/v1/roles'), enabled });
}
export function useMembers(enabled = true) {
  return useQuery({ queryKey: queryKeys.members(), queryFn: () => api<{ items: MemberDTO[] }>('/api/v1/members'), enabled });
}
/** Access → Members (1.2.1): filters, as the API takes them. Empty values are left out. */
export interface MembersParams { q: string; role: string; groups: string[]; status: 'all' | 'active' | 'disabled' }

/** The Members list filtered and paged (50 at a time); the members() key prefix still refreshes it. */
export function useMembersPage(params: MembersParams) {
  const u = new URLSearchParams();
  if (params.q) u.set('q', params.q);
  if (params.role) u.set('role', params.role);
  if (params.groups.length) u.set('group', params.groups.join(','));
  if (params.status !== 'all') u.set('status', params.status);
  return usePaged<MemberDTO, { items: MemberDTO[]; nextCursor?: string | null; total?: number }>({
    queryKey: [...queryKeys.members(), 'page', u.toString()], url: `/api/v1/members?${u}`, pageSize: 50,
  });
}
export function useAccessGroups(enabled = true) {
  return useQuery({ queryKey: queryKeys.accessGroups(), queryFn: () => api<{ items: AccessGroupDTO[] }>('/api/v1/access-groups'), enabled });
}

export function usePeopleSearch(q: string) {
  return useQuery({
    queryKey: queryKeys.people(q),
    queryFn: () => api<{ items: Array<{ id: string; name: string; email: string }> }>(`/api/v1/people?q=${encodeURIComponent(q)}`),
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });
}
